/**
 * MermaidPreview — renders a Mermaid diagram from raw text. Used by the
 * file viewer for `.mmd` / `.mermaid` files (peer of the markdown preview).
 *
 * The Mermaid library is dynamic-imported on first render so the ~500KB
 * bundle stays out of the main chunk for users who never open a diagram.
 */
import { useEffect, useRef, useState } from 'react';
import { logger } from '@/lib/logger';

const TAG = 'MermaidPreview';

interface MermaidPreviewProps {
  source: string;
}

/** Module-level promise so multiple <MermaidPreview> instances share one
 *  load — and so the init() call only runs once per app lifetime. */
let mermaidReady: Promise<typeof import('mermaid').default> | null = null;
function loadMermaid(): Promise<typeof import('mermaid').default> {
  if (mermaidReady) return mermaidReady;
  mermaidReady = import('mermaid').then((mod) => {
    const mermaid = mod.default;
    mermaid.initialize({
      startOnLoad: false,
      theme: 'dark',
      securityLevel: 'strict',
      // The dark theme reads OK on our dark-grey panels; tune later if needed.
      themeVariables: {
        background: 'transparent',
      },
      fontFamily: 'inherit',
    });
    return mermaid;
  });
  return mermaidReady;
}

export default function MermaidPreview({ source }: MermaidPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    (async () => {
      try {
        const mermaid = await loadMermaid();
        // Each render needs a unique-per-mount id so concurrent previews
        // don't collide in mermaid's internal SVG cache.
        const id = `mermaid-${Math.random().toString(36).slice(2, 10)}`;

        // Validate first — when render() hits a parse error in mermaid 11.x
        // it creates a temp element on document.body to paint the "Syntax
        // error in text" SVG and may leave it there. parse() with
        // suppressErrors avoids touching the DOM at all.
        const parseResult = await mermaid.parse(source, { suppressErrors: true });
        if (cancelled) return;
        if (parseResult === false) {
          setError('Invalid mermaid syntax');
          setSvg(null);
          return;
        }

        const result = await mermaid.render(id, source);
        if (cancelled) return;
        setSvg(result.svg);
      } catch (e) {
        if (cancelled) return;
        logger.warn(TAG, 'Mermaid render failed', e);
        setError(e instanceof Error ? e.message : String(e));
        setSvg(null);
      } finally {
        // Belt-and-suspenders: nuke any temp render elements mermaid
        // leaked onto document.body. Mermaid uses `d<id>` for its temp
        // wrapper plus a `dmermaid-…` id pattern in 11.x.
        document.querySelectorAll('body > [id^="dmermaid-"], body > [id^="d-mermaid-"]').forEach((n) => n.remove());
      }
    })();

    return () => { cancelled = true; };
  }, [source]);

  if (error) {
    return (
      <div className="h-full overflow-auto bg-base-100 p-6">
        <div className="alert alert-warning max-w-2xl">
          <div>
            <h3 className="font-semibold mb-1">Mermaid syntax error</h3>
            <pre className="text-xs whitespace-pre-wrap">{error}</pre>
          </div>
        </div>
        <details className="mt-4">
          <summary className="cursor-pointer text-xs text-base-content/60">Show source</summary>
          <pre className="mt-2 text-xs bg-base-200 p-3 rounded overflow-auto">{source}</pre>
        </details>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="h-full flex items-center justify-center text-base-content/50">
        <span className="loading loading-spinner loading-md" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full overflow-auto bg-base-100 p-6 flex justify-center items-start"
      // SVG comes from mermaid; securityLevel: 'strict' above sandboxes
      // any embedded HTML — safe to inject.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
